export type RequestStatus =
  | "manage_requests" // Talepleri Yönet
  | "sent_for_approval" // Onaya Gönderildi
  | "approved"; // Onaylandı

export type EducationFormat = "Yüz Yüze" | "Canlı" | "Üniteli" | "Ünitesiz";

export interface RequestAndApprovalEntry {
  id: string;
  educationId: string; // Links to education-management Education for detail drawer
  type: number; // 0 ON_SITE, 1 ONLINE, 2 UNIT, 3 NONUNIT
  educationName: string;
  format: EducationFormat;
  /** totalEnrolledCount / capacity (liste kolonu), örn. "120 / 200" */
  requestCount: string;
  requestStartDate: string;
  requestEndDate: string;
  status: RequestStatus;
  /**
   * Geçerli kullanıcı bu eğitime per-education yönetici olarak atanmış mı?
   * Atanmış EDUCATION_SUPERVISOR'a "Plan Eğitimi" gibi manager-only aksiyonları
   * göstermek için kullanılır.
   */
  isCurrentUserManager?: boolean;
}

export interface RequestAndApprovalFilters {
  search?: string;
  format?: EducationFormat | EducationFormat[];
  status?: RequestStatus | RequestStatus[];
  /** Aynı API (GET /api/admin/educations) filtreleri */
  type?: number | number[];
  categoryIds?: string[];
  instructorIds?: string[];
  publishDate?: string;
}

/** Personnel row on Eğitim Sorumlusu (education officer) screen */
export type PersonnelRequestStatus =
  | "onaylandi" // 2 ACTIVE
  | "ilk_onay_bekliyor" // 0 REQUESTED
  | "ikinci_onay_bekliyor" // 15 REQUESTED
  | "ucuncu_onay_bekliyor" // 1 APPROVED_BRANCH_MANAGER
  | "reddedildi" // 3, 4 REJECTED
  | "iptal_edildi" // 5 CANCELLED
  | "tamamlandi"; // 6 COMPLETED

export interface PersonnelRequestEntry {
  id: string;
  /** Kullanıcı UUID — sanal sınıfa katılımcı ekleme vb. */
  userId: string;
  personelAdi: string;
  tcKimlikNo: string;
  unvan: string;
  birim?: string;
  status: PersonnelRequestStatus;
  /** Kullanıcının aktif kara liste kaydı var mı (sadece bilgi amaçlı; akış engellemez). */
  isBanned?: boolean;
}

/** Department/division for Eğitim Yöneticisi (education manager) accordion */
export interface DepartmentWithPersonnel {
  id: string;
  name: string;
  talepCount: number;
  onayCount: number;
  personnel: PersonnelRequestEntry[];
}

export interface PlanningScheduledTime {
  day: number;
  startTime: string;
  endTime: string;
  timezone?: string;
}

/** POST .../enrollments/approve-with-class-groups gövdesi — scheduledTimes */
export interface ApproveClassGroupScheduledTimePayload {
  day: number;
  startTime: string;
  endTime: string;
}

/** POST .../enrollments/approve-with-class-groups — grup */
export interface ApproveClassGroupPayload {
  name?: string;
  jitsiRoomName?: string;
  scheduledTimes?: ApproveClassGroupScheduledTimePayload[];
  presetLocationId?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  startDate?: string;
  endDate?: string;
  enrollmentIds: string[];
  /** Sanal sınıfa atanacak eğitmenler — eğitime atanmış kümeden alt seçim */
  instructorIds?: string[];
}

export interface ApproveWithClassGroupsPayload {
  groups: ApproveClassGroupPayload[];
}

/** GET .../enrollments/planning-groups — scheduledTimes */
export interface PlanningGroupScheduledTimeApi {
  day: number;
  startTime: string;
  endTime: string;
  timezone?: string;
}

/** GET .../enrollments/planning-groups — katılımcı (enrollment + kullanıcı) */
export interface PlanningGroupEnrollmentFromApi {
  enrollmentId: string;
  userId: string;
  firstName: string;
  lastName: string;
  /** Ünvan; yoksa null */
  title: string | null;
}

/** GET .../enrollments/planning-groups — grup */
export interface PlanningGroupFromApi {
  /** Sanal sınıf PATCH için — backend id / virtualClassroomId / snake_case vb. dönebilir */
  id?: string;
  virtualClassroomId?: string;
  virtualClassroomID?: string;
  name?: string;
  jitsiRoomName?: string;
  scheduledTimes?: PlanningGroupScheduledTimeApi[];
  presetLocationId?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  startDate?: string;
  endDate?: string;
  enrollmentIds: string[];
  /** Varsa katılımcı listesinde ad/soyad/ünvan gösterimi için */
  enrollments?: PlanningGroupEnrollmentFromApi[];
  /** Bu sanal sınıfa atanmış eğitmenler — UI seçici state'i için */
  instructorIds?: string[];
  instructors?: { userID: string; firstName: string | null; lastName: string | null }[];
}

export interface PlanningGroupsResponseApi {
  groups: PlanningGroupFromApi[];
}

export function getPlanningGroupVirtualClassroomId(
  g: PlanningGroupFromApi,
): string | undefined {
  const raw = g as unknown as Record<string, unknown>;
  const tryStr = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() ? v.trim() : undefined;

  const keys = [
    g.id,
    g.virtualClassroomId,
    g.virtualClassroomID,
    raw.virtual_classroom_id,
    raw.classroomId,
    raw.classroomID,
    raw.classroom_id,
    raw.virtualClassroomId,
  ];
  for (const v of keys) {
    const s = tryStr(v);
    if (s) return s;
  }
  return undefined;
}
